import { useState, useRef, useEffect } from "react"
import { motion } from "framer-motion"
import { Send, ArrowLeft, Paperclip, MoreVertical, Phone, Video } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Input } from "@/components/ui/input"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { ScrollArea } from "@/components/ui/scroll-area"
import { useToast } from "@/hooks/use-toast"
import { useNavigate } from 'react-router-dom';

const GroupChat = ({
  currentUser,
  participants = [],
  onBack,
  initialMessages = []
}) => {
  const [messages, setMessages] = useState(initialMessages)
  const [newMessage, setNewMessage] = useState("")
  const messagesEndRef = useRef(null)
  const { toast } = useToast()
  const navigate = useNavigate()

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages])

  const handleSendMessage = () => {
    if (!newMessage.trim()) return

    // Create a new message
    const message = {
      id: Date.now().toString(),
      senderId: currentUser.id,
      text: newMessage,
      timestamp: new Date(),
      status: "sending",
    }

    // Add to messages
    setMessages((prev) => [...prev, message])
    setNewMessage("")

    // Simulate message being delivered
    setTimeout(() => {
      setMessages(
        (prev) => prev.map((m) => (m.id === message.id ? { ...m, status: "delivered" } : m))
      )
    }, 1000)

    // Simulate response from another participant after a delay
    if (messages.length % 3 === 0) {
      const randomParticipant = participants[Math.floor(Math.random() * participants.length)]
      setTimeout(() => {
        const responseMessage = {
          id: (Date.now() + 1).toString(),
          senderId: randomParticipant.id,
          text: `Thanks for the introduction! Looking forward to collaborating.`,
          timestamp: new Date(),
          status: "delivered",
        }
        setMessages((prev) => [...prev, responseMessage])
      }, 3000)
    }
  }

  const getPersonById = id => {
    if (id === currentUser.id) return currentUser
    return participants.find((p) => p.id === id) || currentUser;
  }

  const formatTime = date => {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }

  return (
    <div className="flex flex-col h-screen bg-white">
      {/* Header */}
      <div className="flex items-center p-4 border-b bg-green-50">
        {onBack && (
          <Button variant="ghost" size="icon" onClick={onBack} className="mr-2">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        )}

        <div className="flex -space-x-2 mr-3">
          {participants.map((person, index) => (
            <Avatar
              key={person.id}
              className={`border-2 border-white ${index > 2 ? "hidden" : ""}`}>
              <AvatarImage src={person.avatar} alt={person.name} />
              <AvatarFallback className="bg-green-100 text-green-800">
                {person.name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")}
              </AvatarFallback>
            </Avatar>
          ))}
        </div>

        <div className="flex-1">
          <h2 className="font-semibold text-green-800">Professional Introduction</h2>
          <p className="text-xs text-green-600">{participants.map((p) => p.name).join(", ")}</p>
        </div>

        <div className="flex gap-1">
          <Button variant="ghost" size="icon" className="text-green-700">
            <Phone className="h-5 w-5" />
          </Button>
          <Button variant="ghost" size="icon" className="text-green-700">
            <Video className="h-5 w-5" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="text-green-700">
                <MoreVertical className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem>View group info</DropdownMenuItem>
              <DropdownMenuItem>Mute notifications</DropdownMenuItem>
              <DropdownMenuItem>Search</DropdownMenuItem>
              <DropdownMenuItem className="text-red-600">Leave chat</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      {/* Messages */}
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4">
          {/* Welcome message */}
          <div className="bg-green-50 rounded-lg p-3 text-center mx-auto max-w-md">
            <p className="text-green-800 text-sm">
              This group was created to introduce {participants[0]?.name} to {participants[1]?.name}. You can now start
              the conversation!
            </p>
          </div>

          {messages.map((message) => {
            const sender = getPersonById(message.senderId)
            const isCurrentUser = message.senderId === currentUser.id

            return (
              <div
                key={message.id}
                className={`flex ${isCurrentUser ? "justify-end" : "justify-start"}`}>
                <div
                  className={`flex ${isCurrentUser ? "flex-row-reverse" : "flex-row"} max-w-[80%] items-end gap-2`}>
                  {!isCurrentUser && (
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={sender.avatar} alt={sender.name} />
                      <AvatarFallback className="bg-green-100 text-green-800 text-xs">
                        {sender.name
                          .split(" ")
                          .map((n) => n[0])
                          .join("")}
                      </AvatarFallback>
                    </Avatar>
                  )}

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`rounded-lg p-3 ${
                      isCurrentUser
                        ? "bg-green-500 text-white rounded-br-none"
                        : "bg-gray-100 text-gray-800 rounded-bl-none"
                    }`}>
                    {!isCurrentUser && <p className="text-xs font-medium mb-1 text-green-700">{sender.name}</p>}
                    <p>{message.text}</p>
                    <div
                      className={`text-xs mt-1 flex justify-end ${isCurrentUser ? "text-green-100" : "text-gray-500"}`}>
                      {formatTime(message.timestamp)}
                      {isCurrentUser && (
                        <span className="ml-1">
                          {message.status === "sending" && "●"}
                          {message.status === "sent" && "✓"}
                          {message.status === "delivered" && "✓✓"}
                          {message.status === "read" && "✓✓"}
                        </span>
                      )}
                    </div>
                  </motion.div>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>
      {/* Message input */}
      <div className="p-4 border-t">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="text-green-600">
            <Paperclip className="h-5 w-5" />
          </Button>

          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 border-green-200 focus-visible:ring-green-500"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault()
                handleSendMessage()
              }
            }} />

          <Button
            onClick={handleSendMessage}
            size="icon"
            className="bg-green-600 hover:bg-green-700 text-white"
            disabled={!newMessage.trim()}>
            <Send className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default GroupChat
